import { useMemo } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIsAnyOrthoOverlapShowing } from "plugins/Orthomosaic/OrthomosaicSlice"
import { selectAnyInfraredFlightUsesHexPoints } from "features/entity-caching/flights/selectors"
import { useViewport } from "../GlobalViewState"
import { useOverlays } from "../control/useOverlays"
import type { MapBackgroundViewType } from "../control/useMapStyles"
import { getFireDangerRegionsInView } from "./fireDangerRegions"
import { LEGEND_DEFINITIONS } from "./legendDefinitions"
import { LegendId } from "./types"

export const useVisibleLegendDefinitions = (viewType: MapBackgroundViewType) => {
    const overlays = useOverlays(viewType)
    const hotspotHexIsActive = useAppSelector(selectAnyInfraredFlightUsesHexPoints)
    const orthoOverlapIsActive = useAppSelector(selectIsAnyOrthoOverlapShowing)

    const viewport = useViewport()
    const [west, south, east, north] = viewport.getBounds()
    const { usInView, caInView } = getFireDangerRegionsInView({ west, south, east, north })
    const bothFireDangerRegionsInView = usInView && caInView

    const visibleIds = useMemo(() => {
        const ids = new Set<LegendId>()
        if (overlays.fireDanger.enabled) {
            if (caInView) ids.add("fireDangerCA")
            if (usInView) ids.add("fireDangerUS")
        }
        if (overlays.heat.enabled) ids.add("heat")
        if (overlays.elevation.enabled) ids.add("elevation")
        if (hotspotHexIsActive) {
            ids.add("hotspotTemperature")
            ids.add("hotspotArea")
        }
        if (orthoOverlapIsActive) ids.add("imageOverlap")
        return ids
    }, [
        overlays.fireDanger.enabled,
        overlays.heat.enabled,
        overlays.elevation.enabled,
        caInView,
        usInView,
        hotspotHexIsActive,
        orthoOverlapIsActive,
    ])

    return LEGEND_DEFINITIONS.filter((definition) => visibleIds.has(definition.id)).map(
        (definition) =>
            (definition.id === "fireDangerCA" || definition.id === "fireDangerUS") &&
            !bothFireDangerRegionsInView
                ? { ...definition, title: "Fire Danger" }
                : definition
    )
}
